import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Rocket, LogOut, Volume2, VolumeX, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSound } from "../lib/sound";
import NotificationBell from "./NotificationBell";
import PwaInstallButton from "./PwaInstallButton";
import MobileDownloadButton from "./MobileDownloadButton";
import VolumeControls from "./VolumeControls";
import api from "../lib/api";

// Threshold for the red Admin "ops alert" badge — above this many client
// errors in the last 24h, the navbar pings the admin so they open the
// dashboard before the next regression compounds.
const ADMIN_ALERT_THRESHOLD = 5;

// Skins "NEW" badge state key. The actual catalog version is fetched
// from the backend (/api/ball-skins/catalog-version) so the badge
// re-arms AUTOMATICALLY the moment a new skin family ships — no need
// to remember to bump a constant. Each pilot's localStorage entry
// stores the last catalog version they viewed.
const SKINS_SEEN_KEY = "cwb_skins_seen";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { muted } = useSound();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminAlert, setAdminAlert] = useState(0);
  // "NEW" badge state for the Skins nav link. The current catalog version
  // is fetched from the backend on mount + every 60s. The badge surfaces
  // whenever the latest version doesn't match the pilot's locally-stored
  // last-seen version. Initial state defaults to false (no badge until
  // we've confirmed there IS a newer drop).
  const [latestSkinVersion, setLatestSkinVersion] = useState(null);
  const [seenSkinVersion, setSeenSkinVersion] = useState(() => {
    try {
      return localStorage.getItem(SKINS_SEEN_KEY) || "";
    } catch {
      return "";
    }
  });
  const skinsBadge = !!latestSkinVersion && latestSkinVersion !== seenSkinVersion;
  // Poll the catalog version. Public endpoint — no auth headers needed.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/ball-skins/catalog-version");
        const v = r.data?.catalog_version;
        if (!cancelled && typeof v === "string" && v.length) {
          setLatestSkinVersion(v);
        }
      } catch {
        // Network blip — keep last value.
      }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);
  // Clear the badge the moment the pilot enters /skins. Same 500ms
  // pathname-poll pattern as before so the Navbar stays route-agnostic.
  useEffect(() => {
    if (!skinsBadge) return undefined;
    const checkAndClear = () => {
      if (window.location.pathname.startsWith("/skins") && latestSkinVersion) {
        try {
          localStorage.setItem(SKINS_SEEN_KEY, latestSkinVersion);
        } catch {
          // localStorage disabled — badge will reappear next mount but that's OK.
        }
        setSeenSkinVersion(latestSkinVersion);
      }
    };
    checkAndClear();
    const id = setInterval(checkAndClear, 500);
    return () => clearInterval(id);
  }, [skinsBadge, latestSkinVersion]);
  // Volume control popover — opened by clicking the speaker icon. The
  // panel holds mute toggle + SFX + Music sliders. Closes on outside click.
  const [volOpen, setVolOpen] = useState(false);
  // FTUE flag — when the panel is opened by the first-time audio hint
  // (rather than a manual tap) we want the panel itself to surface a
  // friendly "// HEADPHONES RECOMMENDED 🎧" caption so new pilots
  // understand WHY it's auto-open. Cleared on any manual interaction.
  const [volHint, setVolHint] = useState(false);
  const volRef = useRef(null);
  useEffect(() => {
    if (!volOpen) return undefined;
    const onClick = (e) => {
      if (volRef.current && !volRef.current.contains(e.target)) {
        setVolOpen(false);
        setVolHint(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("touchstart", onClick);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("touchstart", onClick);
    };
  }, [volOpen]);
  // FTUE listener — pages can dispatch `cwb-show-audio-hint` to gently
  // educate brand-new pilots that the audio controls live behind this
  // speaker button. The pages are responsible for the localStorage
  // gate (so the hint never repeats). We just open the panel + show
  // the headphone caption, then auto-close after 3.2s.
  useEffect(() => {
    const onHint = () => {
      setVolHint(true);
      setVolOpen(true);
      // Auto-close so the FTUE doesn't block the game.
      window.setTimeout(() => {
        setVolOpen(false);
        setVolHint(false);
      }, 3200);
    };
    window.addEventListener("cwb-show-audio-hint", onHint);
    return () => window.removeEventListener("cwb-show-audio-hint", onHint);
  }, []);

  // Poll the admin KPI snapshot on mount + every 60s so the navbar
  // surfaces ops regressions without the admin needing to open /admin.
  // 403 (non-admin) silently no-ops; the link itself is also gated to
  // the admin email so non-admins never see the badge.
  useEffect(() => {
    const isAdmin = (user?.email || "").toLowerCase() === "admin@cosmic.space";
    if (!isAdmin) {
      setAdminAlert(0);
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/admin/stats");
        if (!cancelled) setAdminAlert(Number(r.data?.client_errors_24h || 0));
      } catch {
        // Network blip — keep last value, don't flap the badge.
      }
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user?.email]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Compact link style used in both desktop bar AND mobile drawer
  const navLinkClass = "font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 transition hover:text-white";

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="nav-logo-link"
        >
          <div className="w-9 h-9 border border-[#b026ff] flex items-center justify-center transition-all group-hover:shadow-[0_0_14px_rgba(176,38,255,0.8)]">
            <Rocket className="w-4 h-4 text-[#b026ff]" strokeWidth={2.25} />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-heading text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] text-white/80 uppercase">
              Cosmic
            </div>
            <div className="font-heading text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.25em] text-[#00ffff] uppercase -mt-0.5 truncate">
              Worm Breaker
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
          <div className="relative" ref={volRef}>
            <button
              onClick={() => { setVolOpen((v) => !v); setVolHint(false); }}
              className="w-9 h-9 border border-white/15 hover:border-[#00ffff] text-white/80 hover:text-[#00ffff] flex items-center justify-center transition"
              title="Audio settings"
              data-testid="nav-sound-toggle"
              aria-haspopup="dialog"
              aria-expanded={volOpen}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            {volOpen && (
              <VolumeControls
                hint={volHint}
                onClose={() => { setVolOpen(false); setVolHint(false); }}
              />
            )}
          </div>
          {user && user !== false ? (
            <>
              <Link
                to="/levels"
                className="hidden sm:block font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 hover:text-[#00ffff] transition"
                data-testid="nav-levels-link"
              >
                Levels
              </Link>
              <Link
                to="/friends"
                className="hidden sm:block font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 hover:text-[#00ffff] transition"
                data-testid="nav-friends-link"
              >
                Friends
              </Link>
              <Link
                to="/skins"
                className="hidden sm:block font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 hover:text-[#ffd700] transition relative"
                data-testid="nav-skins-link"
              >
                Skins
                {skinsBadge && (
                  <span
                    className="absolute -top-1.5 -right-3 px-1.5 py-[1px] bg-[#ffd700] text-black font-mono-cosmic text-[8px] tracking-[0.18em] font-bold leading-none animate-pulse"
                    style={{ boxShadow: "0 0 8px rgba(255,215,0,0.7)" }}
                    data-testid="nav-skins-new-badge"
                  >
                    NEW
                  </span>
                )}
              </Link>
              <Link
                to="/artifacts"
                className="hidden sm:block font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 hover:text-[#b026ff] transition"
                data-testid="nav-artifacts-link"
              >
                Artifacts
              </Link>
              <Link
                to="/season"
                className="hidden md:block font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 hover:text-[#ff007f] transition"
                data-testid="nav-season-link"
              >
                Season
              </Link>
              <Link
                to="/tournaments"
                className="hidden md:block font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 hover:text-[#ffd700] transition"
                data-testid="nav-tournaments-link"
              >
                Brackets
              </Link>
              <Link
                to="/wanted"
                className="hidden md:block font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 hover:text-[#ff5555] transition"
                data-testid="nav-wanted-link"
              >
                Wanted
              </Link>
              <Link
                to="/achievements"
                className="hidden sm:block font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 hover:text-[#00ff41] transition"
                data-testid="nav-achievements-link"
              >
                Trophies
              </Link>
              <Link
                to="/community"
                className="hidden md:block font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 hover:text-[#b026ff] transition"
                data-testid="nav-community-link"
              >
                Community
              </Link>
              {(user.email || "").toLowerCase() === "admin@cosmic.space" && (
                <Link
                  to="/admin"
                  className="hidden md:flex items-center gap-1.5 font-mono-cosmic text-xs tracking-[0.25em] uppercase text-[#ff007f]/85 hover:text-[#ff007f] transition relative"
                  data-testid="nav-admin-link"
                  title={
                    adminAlert > ADMIN_ALERT_THRESHOLD
                      ? `Admin · Mission Control · ${adminAlert} client errors in last 24h`
                      : "Admin · Mission Control"
                  }
                >
                  Admin
                  {adminAlert > ADMIN_ALERT_THRESHOLD && (
                    <span
                      className="px-1.5 py-0.5 border border-[#ff007f] bg-[#ff007f]/15 text-[#ff007f] font-mono-cosmic text-[9px] tracking-[0.15em] tabular-nums leading-none animate-pulse"
                      style={{ boxShadow: "0 0 8px rgba(255,0,127,0.6)" }}
                      data-testid="nav-admin-alert-badge"
                      aria-label={`${adminAlert} client errors in last 24h`}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#ff007f] mr-1 align-middle" />
                      {adminAlert}
                    </span>
                  )}
                </Link>
              )}
              <span
                className="hidden md:block font-mono-cosmic text-xs tracking-widest text-white/50 uppercase"
                data-testid="nav-user-name"
                title={user.handle || undefined}
              >
                {user.handle || user.name || user.email}
              </span>
              <NotificationBell />
              <PwaInstallButton className="hidden md:inline-flex" />
              <button
                onClick={() => setMobileOpen(true)}
                className="sm:hidden w-9 h-9 border border-white/15 hover:border-[#b026ff] text-white/80 hover:text-[#b026ff] flex items-center justify-center transition"
                title="Menu"
                data-testid="nav-mobile-menu-btn"
              >
                <Menu className="w-4 h-4" />
              </button>
              <button
                onClick={handleLogout}
                className="hidden sm:flex items-center gap-2 px-3 py-2 border border-white/15 hover:border-[#ff007f] text-white/80 hover:text-[#ff007f] transition font-mono-cosmic text-xs tracking-widest uppercase"
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="font-mono-cosmic text-xs tracking-[0.25em] uppercase text-white/70 hover:text-[#00ffff] transition"
                data-testid="nav-login-link"
              >
                Login
              </Link>
              <Link
                to="/register"
                className="btn-neon cyan text-xs"
                style={{ padding: "0.55rem 1rem" }}
                data-testid="nav-register-link"
              >
                Sign Up
              </Link>
              {/* Smart download CTA for logged-out visitors. Hidden on
                  the smallest widths (< sm) where horizontal real-
                  estate is at a premium and the hero already carries
                  a full-size copy. */}
              <MobileDownloadButton
                className="hidden sm:inline-flex !px-3 !py-1.5 !text-[10px]"
                testId="nav-download-btn"
              />
            </>
          )}
        </div>
      </div>

      {user && user !== false && mobileOpen && (
        <div
          className="sm:hidden fixed inset-0 z-[9999]"
          onClick={() => setMobileOpen(false)}
          data-testid="nav-mobile-drawer"
          style={{
            isolation: "isolate",
            // Explicit dynamic-viewport height with cascading fallbacks so
            // the drawer always covers the full visible area on mobile —
            // mobile Chrome / Samsung Internet otherwise leave a gap at the
            // bottom under the address bar / system gesture area.
            height: "100vh",
          }}
        >
          {/* Pure-black backdrop — no transparency, no backdrop-filter */}
          <div
            className="absolute inset-0"
            style={{ background: "#000", height: "100dvh", minHeight: "100vh" }}
          />
          <div
            className="absolute right-0 top-0 w-72 max-w-[80vw] border-l-2 border-[#b026ff] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{
              // Local slide-in keyframe — avoids the shared fadeSlideIn which
              // uses translate(-50%, ...) intended for centered popovers and
              // left the drawer stuck off-screen on Samsung Internet.
              animation: "drawerSlideIn 0.25s ease-out",
              // Fully solid black panel — guaranteed readable on every browser
              background: "#000",
              boxShadow: "-12px 0 32px rgba(176,38,255,0.15)",
              // Use dynamic viewport height so the drawer auto-fits the
              // visible area on mobile (address bar, gesture nav, etc.).
              // `100dvh` covers modern mobile Chrome/Safari; the inline
              // `100vh` keeps older browsers covered too.
              height: "100dvh",
              minHeight: "100vh",
              top: 0,
              bottom: "auto",
            }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#b026ff]/30" style={{ background: "#000" }}>
              <div className="font-mono-cosmic text-[10px] tracking-[0.35em] text-[#b026ff] uppercase">
                // NAV
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 border border-white/30 hover:border-white text-white flex items-center justify-center transition"
                data-testid="nav-mobile-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <nav className="flex-1 px-5 py-4 flex flex-col gap-3 overflow-y-auto" style={{ background: "#000" }} data-testid="nav-mobile-links">
              {[
                { to: "/levels",       label: "Levels",       hover: "hover:text-[#00ffff]" },
                { to: "/play/1",       label: "Quick Play",   hover: "hover:text-[#00ffff]" },
                { to: "/daily",        label: "Daily Run",    hover: "hover:text-[#ffd700]" },
                { to: "/endless",      label: "Endless Mode", hover: "hover:text-[#b026ff]" },
                { to: "/friends",      label: "Friends",      hover: "hover:text-[#00ffff]" },
                { to: "/skins",        label: "Skins",        hover: "hover:text-[#ffd700]" },
                { to: "/artifacts",    label: "Artifacts",    hover: "hover:text-[#b026ff]" },
                { to: "/season",       label: "Season",       hover: "hover:text-[#ff007f]" },
                { to: "/tournaments",  label: "Brackets",     hover: "hover:text-[#ffd700]" },
                { to: "/wanted",       label: "Wanted",       hover: "hover:text-[#ff5555]" },
                { to: "/achievements", label: "Trophies",     hover: "hover:text-[#00ff41]" },
                { to: "/community",    label: "Community",    hover: "hover:text-[#b026ff]" },
                { to: "/builder",      label: "Builder",      hover: "hover:text-[#b026ff]" },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={`font-mono-cosmic uppercase text-white ${item.hover} text-sm tracking-[0.3em] py-2 border-b border-white/15 transition flex items-center justify-between`}
                  data-testid={`nav-mobile-link-${item.to.split("/")[1]}`}
                >
                  <span>{item.label}</span>
                  {item.to === "/skins" && skinsBadge && (
                    <span
                      className="px-1.5 py-[1px] bg-[#ffd700] text-black font-mono-cosmic text-[8px] tracking-[0.18em] font-bold leading-none animate-pulse"
                      style={{ boxShadow: "0 0 8px rgba(255,215,0,0.7)" }}
                      data-testid="nav-mobile-skins-new-badge"
                    >
                      NEW
                    </span>
                  )}
                </Link>
              ))}
              <button
                onClick={() => { setMobileOpen(false); handleLogout(); }}
                className="mt-4 px-3 py-2 border border-[#ff007f] text-[#ff007f] font-mono-cosmic text-xs tracking-widest uppercase inline-flex items-center justify-center gap-2 hover:bg-[#ff007f]/10"
                data-testid="nav-mobile-logout"
              >
                <LogOut className="w-3.5 h-3.5" /> Logout
              </button>
              <div className="mt-3 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/60 text-center">
                {user.handle || user.name || user.email}
              </div>
            </nav>
          </div>
        </div>
      )}
    </nav>
  );
}
